import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { shallowEqual, useSelector } from 'react-redux';
import {
  GUARANTEES_APPLICATION_AM_REVIEW_MODE,
  GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM,
  GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  buildGuaranteesApplicationAmApprovalLetterManualSignPath,
  buildGuaranteesApplicationAmApprovalLetterPath,
  buildGuaranteesApplicationPath,
  buildGuaranteesApplicationStep2Path,
  buildGuaranteesApplicationStep3Path,
} from '~/modules/guarantees/utils.ts';
import { isGuaranteesApprovalLetterPaperExecutionCheck } from '../../shared/approvalLetterExecution.ts';
import { isManagerApprovalLetterManualSignStatus } from '../step6/utils.ts';
import {
  selectGuaranteesAdministrationAccessMap,
  selectHasGuaranteesAmAccess,
} from '~/store/guarantees/selectors.ts';
import type { TGuaranteesApplication } from '~/store/guarantees/types.ts';
import GuaranteesStepNavigator from '../../stepNavigation/GuaranteesStepNavigator.tsx';
import type { TGuaranteesStepNavigationStyle } from '../../stepNavigation/types.ts';
import { guaranteesManagerStepNavigationConfig } from './constants.ts';
import type {
  TGuaranteesManagerStepKey,
  TGuaranteesManagerStepNavigationAccess,
} from './types.ts';

type TGuaranteesManagerStepNavigatorProps = {
  application?: TGuaranteesApplication | null;
  applicationId: string;
  contragentId: string;
  currentStepKey: TGuaranteesManagerStepKey;
  containerStyles?: TGuaranteesStepNavigationStyle;
  stepperStyles?: TGuaranteesStepNavigationStyle;
};

const GuaranteesManagerStepNavigator = ({
  application,
  applicationId,
  contragentId,
  currentStepKey,
  containerStyles,
  stepperStyles,
}: TGuaranteesManagerStepNavigatorProps) => {
  const location = useLocation();
  const administrationAccessMap = useSelector(
    selectGuaranteesAdministrationAccessMap,
    shallowEqual
  );
  const hasAmAccess = useSelector(selectHasGuaranteesAmAccess);
  const reviewMode = new URLSearchParams(location.search).get(
    GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM
  );
  const isManualTaskNavigation =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM) ===
    'true';
  const isAmReviewNavigation =
    currentStepKey === 'amApprovalLetter' ||
    reviewMode === GUARANTEES_APPLICATION_AM_REVIEW_MODE;
  const isPaperApplication = isGuaranteesApprovalLetterPaperExecutionCheck(application);
  const isPaperManualSignApplication =
    isPaperApplication && isManagerApprovalLetterManualSignStatus(application?.applicationStatus);
  const reviewModeParam = isAmReviewNavigation ? GUARANTEES_APPLICATION_AM_REVIEW_MODE : undefined;
  const appendManualTaskParam = useCallback((path: string) => {
    if (!isManualTaskNavigation) {
      return path;
    }

    const separator = path.includes('?') ? '&' : '?';

    return `${path}${separator}${GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM}=true`;
  }, [isManualTaskNavigation]);
  const stepConfig = useMemo(
    () => {
      if (isPaperApplication) {
        return guaranteesManagerStepNavigationConfig;
      }

      const accessMap = {
        ...administrationAccessMap,
        am: hasAmAccess,
      } satisfies TGuaranteesManagerStepNavigationAccess;

      return guaranteesManagerStepNavigationConfig.filter(({ key, requiresAccess }) => {
        const hasRequiredAccess = !requiresAccess || accessMap[requiresAccess];

        if (key === 'amApprovalLetter') {
          return hasRequiredAccess && isAmReviewNavigation;
        }

        return hasRequiredAccess;
      });
    },
    [administrationAccessMap, hasAmAccess, isAmReviewNavigation, isPaperApplication]
  );
  const pathByKey = useMemo(
    () => ({
      step1: appendManualTaskParam(
        buildGuaranteesApplicationPath(applicationId, contragentId, true, reviewModeParam)
      ),
      step2: appendManualTaskParam(
        buildGuaranteesApplicationStep2Path(applicationId, contragentId, true, reviewModeParam)
      ),
      udbtf: appendManualTaskParam(
        buildGuaranteesApplicationStep3Path(applicationId, contragentId, true, reviewModeParam)
      ),
      amApprovalLetter: appendManualTaskParam(
        isPaperManualSignApplication
          ? buildGuaranteesApplicationAmApprovalLetterManualSignPath(applicationId, contragentId)
          : buildGuaranteesApplicationAmApprovalLetterPath(applicationId, contragentId, true)
      ),
    }),
    [
      applicationId,
      appendManualTaskParam,
      contragentId,
      isPaperManualSignApplication,
      reviewModeParam,
    ]
  );

  return (
    <GuaranteesStepNavigator
      containerId="guaranteesManagerStepsContainer"
      currentStepKey={currentStepKey}
      items={stepConfig}
      pathByKey={pathByKey}
      containerStyles={containerStyles}
      stepperStyles={stepperStyles}
    />
  );
};

export default GuaranteesManagerStepNavigator;
